import React, { useState, useRef, useMemo, useEffect } from "react";
import { Plus, X, BookOpen, Edit2 } from "lucide-react";
import Swal from "sweetalert2";
import JoditEditor from 'jodit-react';
import axios from 'axios';

// Base URL for API - adjust according to your Django server
const API_BASE_URL = 'https://backend.dlapakistan.com/api';

const EducationPage = () => {
  const [contents, setContents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    title: "",
    content: ""
  });

  const editorRef = useRef(null);
  const emerald600 = "#059669";

  const config = useMemo(() => ({
    readonly: false,
    height: 300,
    theme: "default",
    toolbarButtonSize: "medium",
    removeButtons: ['source', 'fullsize', 'about', 'print', 'preview', 'find'],
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough',
      'ul', 'ol', 'outdent', 'indent',
      'font', 'fontsize', 'brush', 'paragraph',
      'image', 'table', 'link', 'align',
      'undo', 'redo', 'hr', 'eraser', 'copyformat',
      'selectall'
    ],
    style: { background: '#f9fafb' },
    uploader: { insertImageAsBase64URI: true },
    placeholder: 'Content here...',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    disablePlugins: ['paste', 'pasteStorage'],
  }), []);

  // Fetch data from backend on component mount
  useEffect(() => {
    fetchEducationData();
  }, []);

  const fetchEducationData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/education/`);
      if (response.data) {
        // Transform backend data to match frontend format
        const transformedData = [{
          id: 1,
          title: response.data.heading,
          content: response.data.content
        }];
        setContents(transformedData);
        
        // Also save to localStorage for offline access (optional)
        localStorage.setItem("education_Storage", JSON.stringify(transformedData));
      } else {
        // If no data in backend, check localStorage
        const savedData = localStorage.getItem("education_Storage");
        if (savedData) {
          setContents(JSON.parse(savedData));
        }
      }
    } catch (error) {
      console.error("Error fetching education data:", error);
      // Fallback to localStorage if API fails
      const savedData = localStorage.getItem("education_Storage");
      if (savedData) {
        setContents(JSON.parse(savedData));
      }
      
      // Show error only if it's not 404 (no data yet)
      if (error.response?.status !== 404) {
        Swal.fire({
          icon: "error",
          title: "Connection Error",
          text: "Could not fetch data from server. Using local data.",
          confirmButtonColor: emerald600
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingContent(null);
    setForm({ title: "", content: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill in all fields before saving.",
        confirmButtonColor: emerald600
      });
      return;
    }

    setSaving(true);

    try {
      const dataToSend = {
        heading: form.title.trim(),
        content: form.content.trim()
      };

      let response;
      
      if (contents.length === 0) {
        // Create new - POST request
        response = await axios.post(`${API_BASE_URL}/education/`, dataToSend);
      } else {
        // Update existing - PUT request
        response = await axios.put(`${API_BASE_URL}/education/`, dataToSend);
      }

      // Update local state with response data
      const newContent = { 
        id: 1, 
        title: response.data.heading, 
        content: response.data.content
      };

      const updatedContents = [newContent];
      setContents(updatedContents);
      localStorage.setItem("education_Storage", JSON.stringify(updatedContents));

      Swal.fire({
        icon: "success",
        title: contents.length === 0 ? "Created Successfully!" : "Updated Successfully!",
        showConfirmButton: false,
        timer: 1500
      });

      closeModal();
    } catch (error) {
      console.error("Error saving data:", error);
      
      let errorMessage = "An error occurred while saving. Please try again.";
      
      if (error.response?.status === 400 && contents.length === 0) {
        // If trying to create when record already exists
        errorMessage = "Content already exists. Please use edit instead.";
      }
      
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: errorMessage,
        confirmButtonColor: emerald600
      });
      
      // Fallback to localStorage if API fails
      const newContent = { 
        id: editingContent?.id || Date.now(), 
        title: form.title.trim(), 
        content: form.content.trim()
      };
      const updatedContents = [newContent];
      setContents(updatedContents);
      localStorage.setItem("education_Storage", JSON.stringify(updatedContents));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    if (contents.length > 0) {
      const content = contents[0];
      setEditingContent(content);
      setForm({
        title: content.title || "",
        content: content.content || ""
      });
      setShowModal(true);
    }
  };

  const handleEditorChange = (newContent) => {
    setForm(prev => ({ ...prev, content: newContent }));
  };

  if (loading) {
    return (
      <div className="flex flex-col mt-20 min-h-screen w-full items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        <p className="mt-4 text-gray-600">Loading education content...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      {/* HEADER */}
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">Education Management</h1>
            <p className="text-gray-600 mt-1">Manage education content from database</p>
          </div>
          <button
            onClick={contents.length === 0 ? () => setShowModal(true) : handleEdit}
            style={{ backgroundColor: emerald600 }}
            className="flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all"
          >
            {contents.length === 0 ? <><Plus className="w-5 h-5 mr-2" /> Add</> : <><Edit2 className="w-4 h-4 mr-2" /> Edit Content</>}
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="px-8 md:px-12 mt-16 w-full pb-20">
        {contents.length === 0 ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
            <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-400 text-lg">No content added yet.</p>
            <button
              onClick={() => setShowModal(true)}
              style={{ backgroundColor: emerald600 }}
              className="mt-4 px-6 py-2 rounded-lg text-white font-medium hover:opacity-90 transition-all"
            >
              Add Content
            </button>
          </div>
        ) : (
          <div className="animate-fadeIn text-left w-full bg-transparent">
            <div className="mb-10 border-l-4 pl-6" style={{ borderColor: emerald600 }}>
              <h2 className="text-4xl font-bold text-gray-900 tracking-tight">{contents[0]?.title || "No Title"}</h2>
            </div>
            <div className="mt-12 border-t border-gray-200 pt-10 w-full bg-transparent">
              <div
                className="text-gray-600 text-lg leading-loose space-y-6 custom-description w-full prose prose-emerald max-w-none bg-transparent"
                dangerouslySetInnerHTML={{ __html: contents[0]?.content || "" }}
              />
            </div>
          </div>
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">{contents.length > 0 ? "Update Content" : "Create Content"}</h2>
                <button onClick={closeModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Heading</label>
                  <input 
                    value={form.title} 
                    onChange={(e) => setForm({...form, title: e.target.value})} 
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 transition-all" 
                    style={{ '--tw-ring-color': emerald600 }} 
                    required 
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Content</label>
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden jodit-editor-container">
                    <JoditEditor
                      ref={editorRef}
                      value={form.content}
                      config={config}
                      onChange={handleEditorChange}
                      onBlur={(newContent) => {
                        if (newContent !== form.content) setForm(prev => ({ ...prev, content: newContent }));
                      }}
                    />
                  </div>
                </div>
                <button 
                  type="submit" 
                  style={{ backgroundColor: emerald600 }} 
                  className={`w-full py-4 text-white rounded-2xl font-bold shadow-xl hover:brightness-110 transition-all text-lg flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : contents.length > 0 ? 'Update Content' : 'Create Content'}
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes modalIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-modalIn { animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .custom-description * { background-color: transparent !important; border: none !important; }
        .custom-description ul { list-style-type: disc; padding-left: 1.5rem; }
        .custom-description ol { list-style-type: decimal; padding-left: 1.5rem; }
        .jodit-editor-container :global(.jodit-container) { border: none !important; background: #f9fafb !important; min-height: 300px; }
        .jodit-editor-container :global(.jodit-workplace) { background: #f9fafb !important; min-height: 250px; }
        .jodit-editor-container :global(.jodit-toolbar__box) { background: #f9fafb !important; border-bottom: 1px solid #eee !important; }
        .jodit-editor-container :global(.jodit-wysiwyg) { background: #f9fafb !important; padding: 16px !important; min-height: 200px; }
      `}</style>
    </div>
  );
};

export default EducationPage;